import Moment from 'moment'
import QueryString from 'qs'
import {useCallback, useEffect, useState} from 'react'
import {Helmet} from 'react-helmet'
import {Link, Outlet, Route, Routes, useLocation, useNavigate} from 'react-router-dom'
import {MenuComponent} from '../../../../_metronic/assets/ts/components'
import {ID, KTSVG, useDebounce} from '../../../../_metronic/helpers'
import {PageLink, PageTitle} from '../../../../_metronic/layout/core'
import * as a from '../../../_ancientLibrary/components/'
import {
  ConfirmModal,
  MODAL_TYPES,
  useAncientRequest,
  HttpMethod,
  useMessageModalContext,
} from '../../../_ancientLibrary/utilities'
import {useAuth} from '../../../modules/auth'
import {CustomReportModel} from '../../_models/customReportModels'
import {Toolbar} from "../../../../_metronic/layout/components/toolbar/Toolbar";

const usersBreadcrumbs: Array<PageLink> = [
  {
    title: 'Home',
    path: '/dashboard',
    isSeparator: false,
    isActive: false,
  },
]

export const REPORT_BUILDER = 'reportBuilder'

const ReportBuilderList = () => {
  const [reportList, setReportList] = useState<Array<CustomReportModel>>([])
  const [totalCount, setTotalCount] = useState<number>(0)
  const [pageSize, setPageSize] = useState<number>(100)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [reportToDelete, setReportToDelete] = useState<CustomReportModel>()
  
  //Search Bar
  const [searchText, setSearchText] = useState<string>('')
  const debouncedSearchText = useDebounce(searchText, 150)


  const location = useLocation()
  const navigate = useNavigate()
  const ancientRequest = useAncientRequest()
  const {isAdmin} = useAuth()
  const [isUserModalOpen, setIsUserModalOpen] = useState(false)
  const [isSubmittingCopiedReport, setIsSubmittingCopiedReport] = useState(false)
  const [reportIdToCopy, setReportIdToCopy] = useState<ID>()
  const {showModal} = useMessageModalContext()

  // Update the loadReports callback
  const loadReports = useCallback(() => {
    const queryParams = debouncedSearchText ? `?searchText=${encodeURIComponent(debouncedSearchText)}` : ''
    ancientRequest(HttpMethod.GET, `/bm/${REPORT_BUILDER}/GetReports/${pageSize}/${currentPage}${queryParams}`).then(
        (response) => {
          setReportList(response.data?.queryResult)
          setTotalCount(response.data?.totalCount)
        }
    )
  }, [pageSize, currentPage, debouncedSearchText, ancientRequest])
  
  useEffect(() => {
    setCurrentPage(1)
    loadReports()
  }, [debouncedSearchText, loadReports])

  const onPageChange = (page: number) => {
    const searchParams = new URLSearchParams(location.search)
    searchParams.set('pageNumber', page.toString())
    setCurrentPage(page)
  }

  const createReport = () => {
    navigate(`/c/${REPORT_BUILDER}/report/0`)
  }

  const editReport = (report: CustomReportModel) => {
    navigate(`/c/${REPORT_BUILDER}/report/` + report.customReportID)
  }

  const deleteReport = async (report: CustomReportModel) => {
    if (!reportToDelete) {
      setReportToDelete(report)
      return
    }

    const isSuccess = await ancientRequest(
        HttpMethod.PUT,`/bm/${REPORT_BUILDER}/deleteReport`, report,
      'The report has been deleted.'
    )
    if (isSuccess) loadReports()
  }

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value)
  }



  const handleCloseUserModal = () => {
    setIsUserModalOpen(false)
    setReportIdToCopy(undefined)
  }

  const handleCopyReportToUser = async (userId: ID) => {
    try {
      const queryString = QueryString.stringify({userID: userId})
      await ancientRequest(HttpMethod.PUT, `/bm/${REPORT_BUILDER}/copyReport/${reportIdToCopy}?${queryString}`)
      loadReports()
      setReportIdToCopy(undefined)
      showModal(MODAL_TYPES.SUCCESS_TOAST, {message: `The Report has been copied to the user.`})
      setIsSubmittingCopiedReport(false)
      setIsUserModalOpen(false)
    } catch (e) {
      console.log(e)
    }
  }

  useEffect(() => {
    MenuComponent.reinitialization()
  }, [currentPage, reportList])

  const reportForm = () => {
    return (
      <>
        <Helmet>
          <title>Custom Reports</title>
        </Helmet>
        <PageTitle breadcrumbs={usersBreadcrumbs}>Custom Reports</PageTitle>
        <a.AncientCard>
          <a.AncientCardHeader>
            <span>
              <div className='position-relative'>
                <KTSVG
                    path='/media/icons/duotune/general/gen021.svg'
                    className='svg-icon-2 svg-icon-lg-1 svg-icon-gray-500 position-absolute top-50 ms-5 translate-middle-y'
                />
                <input
                    type='text'
                    className='form-control form-control-solid ps-15 w-500px'
                    placeholder='Search by Report Name or Assigned User'
                    value={searchText}
                    onChange={handleSearch}
                />
              </div>
            </span>
            <div>
              <div className='d-flex align-items-center justify-content-between w-100'>
                <div className='d-flex align-items-center'>
                  <a.AncientButton
                      onClick={createReport}
                      displayText='Create a Report'
                      icon='/media/icons/duotune/arrows/arr075.svg'
                  />
                </div>
              </div>
            </div>
          </a.AncientCardHeader>

          <a.AncientCardBody>
            <a.AncientTable id='reportTable'>
              <a.THead>
                <a.TR contentType='head'>
                  <a.TH>Name</a.TH>
                  <a.TH>Type</a.TH>
                  <a.TH>Assigned User</a.TH>
                  <a.TH>Date Created</a.TH>
                  <a.TH className='text-end'>Actions</a.TH>
                </a.TR>
              </a.THead>
              <a.TBody>
                {reportList?.map((report) => (
                  <a.TR key={report.customReportID}>
                    <a.TD
                      className='cursor-pointer'
                      onClick={() => {
                        editReport(report)
                      }}
                    >
                      {report.reportName}
                    </a.TD>
                    <a.TD>{report.reportType}</a.TD>
                    <a.TD>{report.userEmail || 'N/A'}</a.TD>
                    <a.TD>{Moment(report.dateCreated).format('MM/DD/YYYY')}</a.TD>
                    <a.TD>
                      <div className='d-flex justify-content-end flex-shrink-0 me-n6'>
                        <span
                          className='btn btn-light btn-active-light-secondary btn-sm me-1'
                          data-kt-menu-trigger='click'
                          data-kt-menu-placement='bottom-end'
                          style={{filter: 'drop-shadow(0px 1px 1px #cdcdcd)'}}
                        >
                          Actions
                          <KTSVG
                            path='/media/icons/duotune/arrows/arr072.svg'
                            className='svg-icon-5 m-0'
                          />
                        </span>
                        <div
                          className='menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-600 menu-state-bg-light-primary fw-bold fs-7 w-125px py-4'
                          data-kt-menu='true'
                        >
                          <div className='menu-item px-3'>
                            <Link
                              to={`/c/reportBuilder/report/` + report.customReportID}
                              target='_blank'
                              className='menu-link px-3'
                            >
                              Edit
                            </Link>
                          </div>
                          {isAdmin && (
                            <div
                              className='menu-item px-3'
                              onClick={() => {
                                setIsUserModalOpen(true)
                                setReportIdToCopy(report.customReportID)
                              }}
                            >
                              <span className='menu-link px-3'>Copy to User</span>
                            </div>
                          )}
                          <div className='menu-item px-3'>
                            <span
                              className='menu-link px-3'
                              onClick={() => {
                                deleteReport(report)
                              }}
                            >
                              Delete
                            </span>
                          </div>
                        </div>
                      </div>
                    </a.TD>
                  </a.TR>
                ))}
              </a.TBody>
            </a.AncientTable>
            <a.AncientListPagination
              totalCount={totalCount}
              pageSize={pageSize}
              currentPage={currentPage}
              onPageChange={onPageChange}
            />
          </a.AncientCardBody>
        </a.AncientCard>
        {reportToDelete && (
          <ConfirmModal
            onClose={() => setReportToDelete(undefined)}
            title='Pending Delete'
            message={`Are you sure you want to delete the report "${reportToDelete?.reportName}"?`}
            onOk={() => deleteReport(reportToDelete)}
          />
        )}

        <a.AncientUserLookup
          isOpen={isUserModalOpen}
          onClose={handleCloseUserModal}
          onSelectUser={handleCopyReportToUser}
          isSubmitting={isSubmittingCopiedReport}
          onSubmittingChange={setIsSubmittingCopiedReport}
        />
      </>
    )
  }

  return (
    <Routes>
      <Route element={<Outlet />}>
        <Route path='*' element={reportForm()} />
      </Route>
    </Routes>
  )
}

export default ReportBuilderList
